import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPrivacySettings,
  updatePrivacySettings,
} from "@/data/requests/profile-privacy-requests";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";

export function PrivacySettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: privacyResponse, isLoading } = useQuery({
    queryKey: ["privacy-settings"],
    queryFn: getPrivacySettings,
  });

  const [settings, setSettings] = useState({
    profileVisibility: "public",
    showStreak: true,
    showTaskCount: true,
    showHabitCount: true,
    showLevel: true,
    showBadges: true,
  });

  useEffect(() => {
    if (privacyResponse?.data?.data) {
      setSettings(privacyResponse.data.data);
    } else if (privacyResponse?.data) {
      setSettings(privacyResponse.data);
    }
  }, [privacyResponse]);

  const updateMutation = useMutation({
    mutationFn: updatePrivacySettings,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["privacy-settings"] });
      toast({ title: t("privacySettings.updated") });
      if (data?.data?.data) {
        setSettings(data.data.data);
      }
    },
    onError: () => {
      toast({
        title: t("privacySettings.updateFailed"),
        variant: "destructive",
      });
    },
  });

  const handleVisibilityChange = (value) => {
    const newSettings = { ...settings, profileVisibility: value };
    setSettings(newSettings);
    updateMutation.mutate(newSettings);
  };

  const handleToggleChange = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    updateMutation.mutate(newSettings);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
              <Skeleton className="h-6 w-10" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("privacySettings.title")}</CardTitle>
        <CardDescription>{t("privacySettings.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>{t("privacySettings.profileVisibility")}</Label>
          <Select
            value={settings.profileVisibility}
            onValueChange={handleVisibilityChange}
            disabled={updateMutation.isPending}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={t("privacySettings.visibilityPlaceholder")}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">
                {t("privacySettings.visibility.public")}
              </SelectItem>
              <SelectItem value="followers">
                {t("privacySettings.visibility.followers")}
              </SelectItem>
              <SelectItem value="private">
                {t("privacySettings.visibility.private")}
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t("privacySettings.profileVisibilityDesc")}
          </p>
        </div>

        <div className="space-y-4 pt-4 border-t">
          <h4 className="font-medium text-sm">
            {t("privacySettings.activityTitle")}
          </h4>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t("privacySettings.showStreak")}</Label>
              <p className="text-sm text-muted-foreground">
                {t("privacySettings.showStreakDesc")}
              </p>
            </div>
            <Switch
              checked={settings.showStreak}
              onCheckedChange={() => handleToggleChange("showStreak")}
              disabled={updateMutation.isPending}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t("privacySettings.showTaskCount")}</Label>
              <p className="text-sm text-muted-foreground">
                {t("privacySettings.showTaskCountDesc")}
              </p>
            </div>
            <Switch
              checked={settings.showTaskCount}
              onCheckedChange={() => handleToggleChange("showTaskCount")}
              disabled={updateMutation.isPending}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t("privacySettings.showHabitCount")}</Label>
              <p className="text-sm text-muted-foreground">
                {t("privacySettings.showHabitCountDesc")}
              </p>
            </div>
            <Switch
              checked={settings.showHabitCount}
              onCheckedChange={() => handleToggleChange("showHabitCount")}
              disabled={updateMutation.isPending}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t("privacySettings.showLevel")}</Label>
              <p className="text-sm text-muted-foreground">
                {t("privacySettings.showLevelDesc")}
              </p>
            </div>
            <Switch
              checked={settings.showLevel}
              onCheckedChange={() => handleToggleChange("showLevel")}
              disabled={updateMutation.isPending}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t("privacySettings.showBadges")}</Label>
              <p className="text-sm text-muted-foreground">
                {t("privacySettings.showBadgesDesc")}
              </p>
            </div>
            <Switch
              checked={settings.showBadges}
              onCheckedChange={() => handleToggleChange("showBadges")}
              disabled={updateMutation.isPending}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
